'use client';

import { useEffect, useRef, useState } from 'react';
import { useClerk, useSession, useSignIn, useSignUp } from '@clerk/nextjs';
import { type SetActiveNavigate } from '@clerk/nextjs/types';
import { authRoute, completeAuthNavigation, rememberAuthDestination } from '@/lib/auth/auth-navigation';
import { authFailure, checkAuthResult } from './auth-errors';
import { type AuthChallenge, type AuthFlowOptions, type AuthStep } from './auth.types';
import { sessionTransition } from '@/lib/auth/session-transition';

export const useAuthentication = ({ mode, destination }: AuthFlowOptions) => {
  const clerk = useClerk();
  const { isLoaded, session } = useSession();
  const { signIn, errors: signInErrors } = useSignIn();
  const { signUp, errors: signUpErrors } = useSignUp();
  const [step, setStep] = useState<AuthStep>('start');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const running = useRef(false);
  const mounted = useRef(true);
  const initialized = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const update = () => { setSecondsRemaining(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000))); };
    update();
    if (resendAt <= Date.now()) {
      return;
    }
    const timer = window.setInterval(update, 1000);
    return () => { window.clearInterval(timer); };
  }, [resendAt]);

  const run = async (action: () => Promise<void>) => {
    if (running.current || googlePending || !isLoaded) {
      return;
    }
    running.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (failure) {
      sessionTransition.cancelExpectedNavigation();
      if (mounted.current) {
        setGooglePending(false);
        setError(authFailure(failure).message);
        setCode('');
      }
    } finally {
      running.current = false;
      if (mounted.current) {
        setBusy(false);
      }
    }
  };

  const navigate: SetActiveNavigate = ({ session: nextSession, decorateUrl }) => {
    setPassword('');
    setCode('');
    setStep('leaving');
    if (nextSession.currentTask) {
      rememberAuthDestination(destination);
      window.location.assign(decorateUrl(authRoute('/session-tasks/', destination)));
      return;
    }
    completeAuthNavigation(destination, decorateUrl);
  };

  const showCode = (next: AuthChallenge, sent = true) => {
    setPassword('');
    setCode('');
    setChallenge(next);
    setResendAt(Date.now() + (sent ? 30_000 : 0));
    setStep('code');
  };

  const unsupported = () => {
    setStep('unsupported');
    setError('Your account requires an authentication method that is not available here. Try another method or contact support.');
  };

  const finishSignUp = async (resume = false) => {
    if (signUp.isTransferable) {
      checkAuthResult(await signIn.create({ transfer: true }));
      await finishSignIn(resume);
      return;
    }
    if (signUp.status === 'complete') {
      sessionTransition.expectNavigation();
      checkAuthResult(await signUp.finalize({ navigate }));
      return;
    }
    if (signUp.missingFields.some((field) => { return ['password', 'first_name', 'last_name', 'legal_accepted', 'email_address'].includes(field); })) {
      setStep('details');
      return;
    }
    if (signUp.unverifiedFields.includes('email_address')) {
      if (!resume) {
        checkAuthResult(await signUp.verifications.sendEmailCode());
      }
      showCode({ kind: 'sign-up', destination: signUp.emailAddress || email }, !resume);
      return;
    }
    unsupported();
  };

  const chooseFactor = async (factor: AuthChallenge) => {
    switch (factor.kind) {
      case 'email': checkAuthResult(await signIn.emailCode.sendCode({ emailAddressId: factor.factorId })); break;
      case 'phone': checkAuthResult(await signIn.phoneCode.sendCode({ phoneNumberId: factor.factorId })); break;
      case 'mfa-email': checkAuthResult(await signIn.mfa.sendEmailCode()); break;
      case 'mfa-phone': checkAuthResult(await signIn.mfa.sendPhoneCode()); break;
      default: break;
    }
    showCode(factor);
  };

  const finishSignIn = async (resume = false) => {
    if (signIn.isTransferable) {
      checkAuthResult(await signUp.create({ transfer: true }));
      await finishSignUp(resume);
      return;
    }
    if (signIn.status === 'complete') {
      sessionTransition.expectNavigation();
      checkAuthResult(await signIn.finalize({ navigate }));
      return;
    }
    if (signIn.status === 'needs_new_password') {
      setPassword('');
      setStep('reset-password');
      return;
    }
    if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust') {
      setPassword('');
      setStep('factors');
      return;
    }
    if (signIn.status === 'needs_first_factor') {
      const verification = signIn.firstFactorVerification;
      if (resume && verification.strategy === 'reset_password_email_code') {
        showCode({ kind: 'reset', destination: signIn.identifier || email }, false);
        return;
      }
      const emailFactor = signIn.supportedFirstFactors.find((factor) => { return factor.strategy === 'email_code'; });
      if (resume && verification.strategy === 'email_code' && emailFactor) {
        showCode({ kind: 'email', destination: emailFactor.safeIdentifier, factorId: emailFactor.emailAddressId }, false);
        return;
      }
      const phoneFactor = signIn.supportedFirstFactors.find((factor) => { return factor.strategy === 'phone_code'; });
      if (resume && verification.strategy === 'phone_code' && phoneFactor) {
        showCode({ kind: 'phone', destination: phoneFactor.safeIdentifier, factorId: phoneFactor.phoneNumberId }, false);
        return;
      }
      if (signIn.supportedFirstFactors.some((factor) => { return factor.strategy === 'password'; })) {
        setStep('password');
        return;
      }
      if (emailFactor && !resume) {
        await chooseFactor({ kind: 'email', destination: emailFactor.safeIdentifier, factorId: emailFactor.emailAddressId });
        return;
      }
      setStep('factors');
      return;
    }
    unsupported();
  };

  useEffect(() => {
    if (!isLoaded || initialized.current) {
      return;
    }
    initialized.current = true;
    rememberAuthDestination(destination);
    if (session?.currentTask) {
      setStep('leaving');
      window.location.replace(authRoute('/session-tasks/', destination));
      return;
    }
    if (session?.status === 'active') {
      setStep('leaving');
      completeAuthNavigation(destination, (url) => { return url; });
      return;
    }
    const callbackError = mode === 'sign-up' ? signUpErrors.global?.[0] : signInErrors.global?.[0];
    if (callbackError) {
      setError(callbackError.message);
      return;
    }
    // Restore Clerk's attempt, never passwords or codes. Resuming must not send another email.
    if ((mode === 'sign-up' || mode === 'claim') && signUp.id && signUp.status !== 'complete') {
      setEmail(signUp.emailAddress || '');
      setFirstName(signUp.firstName || '');
      setLastName(signUp.lastName || '');
      setLegalAccepted(Boolean(signUp.legalAcceptedAt));
      void run(async () => { await finishSignUp(true); });
    } else if (mode !== 'sign-up' && signIn.id && signIn.status !== 'complete' && (mode !== 'recovery' || signIn.firstFactorVerification.strategy === 'reset_password_email_code' || signIn.status === 'needs_new_password')) {
      setEmail(signIn.identifier || '');
      void run(async () => { await finishSignIn(true); });
    }
    // Restore once per mounted form. Subsequent transitions are owned by submitted actions.
  }, [isLoaded]);

  const submit = async (verificationCode = code) => {
    await run(async () => {
      const identifier = email.trim();
      setEmail(identifier);
      if (step === 'start') {
        if (mode === 'sign-up') {
          checkAuthResult(await signIn.reset());
          checkAuthResult(await signUp.create({
            emailAddress: identifier,
            ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
            ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
            ...(password ? { password } : {}),
            ...(legalAccepted ? { legalAccepted: true } : {}),
          }));
          setPassword('');
          await finishSignUp();
        } else if (mode === 'recovery') {
          checkAuthResult(await signIn.create({ identifier }));
          if (!signIn.supportedFirstFactors.some((factor) => { return factor.strategy === 'reset_password_email_code'; })) {
            throw new Error('Password recovery is not available for this account. Go back to sign in with an available method.');
          }
          checkAuthResult(await signIn.resetPasswordEmailCode.sendCode());
          showCode({ kind: 'reset', destination: identifier });
        } else {
          checkAuthResult(await signUp.reset());
          const result = await signIn.create({ identifier });
          if (mode === 'claim' && result.error && authFailure(result.error).code === 'form_identifier_not_found') {
            checkAuthResult(await signUp.create({ emailAddress: identifier }));
            await finishSignUp();
            return;
          }
          checkAuthResult(result);
          await finishSignIn();
        }
      } else if (step === 'password') {
        checkAuthResult(await signIn.password({ emailAddress: identifier, password }));
        setPassword('');
        await finishSignIn();
      } else if (step === 'details') {
        const missing = [...signUp.missingFields];
        if (missing.some((field) => { return ['email_address', 'first_name', 'last_name', 'legal_accepted'].includes(field); })) {
          checkAuthResult(await signUp.update({
            ...(missing.includes('email_address') ? { emailAddress: identifier } : {}),
            ...(missing.includes('first_name') ? { firstName: firstName.trim() } : {}),
            ...(missing.includes('last_name') ? { lastName: lastName.trim() } : {}),
            ...(missing.includes('legal_accepted') ? { legalAccepted } : {}),
          }));
        }
        if (signUp.missingFields.includes('password')) {
          checkAuthResult(await signUp.password({
            emailAddress: signUp.emailAddress || identifier,
            password,
            ...(signUp.firstName ? { firstName: signUp.firstName } : {}),
            ...(signUp.lastName ? { lastName: signUp.lastName } : {}),
            ...(signUp.legalAcceptedAt ? { legalAccepted: true } : {}),
          }));
        }
        setPassword('');
        await finishSignUp();
      } else if (step === 'reset-password') {
        checkAuthResult(await signIn.resetPasswordEmailCode.submitPassword({ password }));
        setPassword('');
        await finishSignIn();
      } else if (step === 'code' && challenge) {
        const value = verificationCode.trim();
        switch (challenge.kind) {
          case 'sign-up':
            checkAuthResult(await signUp.verifications.verifyEmailCode({ code: value }));
            await finishSignUp();
            return;
          case 'email': checkAuthResult(await signIn.emailCode.verifyCode({ code: value })); break;
          case 'phone': checkAuthResult(await signIn.phoneCode.verifyCode({ code: value })); break;
          case 'mfa-email': checkAuthResult(await signIn.mfa.verifyEmailCode({ code: value })); break;
          case 'mfa-phone': checkAuthResult(await signIn.mfa.verifyPhoneCode({ code: value })); break;
          case 'totp': checkAuthResult(await signIn.mfa.verifyTOTP({ code: value })); break;
          case 'backup': checkAuthResult(await signIn.mfa.verifyBackupCode({ code: value })); break;
          case 'reset':
            checkAuthResult(await signIn.resetPasswordEmailCode.verifyCode({ code: value }));
            setCode('');
            setPassword('');
            setStep('reset-password');
            return;
        }
        await finishSignIn();
      }
    });
  };

  const resendCode = async () => {
    await run(async () => {
      if (!challenge || Date.now() < resendAt) {
        return;
      }
      if (challenge.kind === 'sign-up') {
        checkAuthResult(await signUp.verifications.sendEmailCode());
      } else if (challenge.kind === 'reset') {
        checkAuthResult(await signIn.resetPasswordEmailCode.sendCode());
      } else if (challenge.kind !== 'totp' && challenge.kind !== 'backup') {
        await chooseFactor(challenge);
      } else {
        return;
      }
      setCode('');
      setResendAt(Date.now() + 30_000);
      setNotice('A new code has been sent.');
    });
  };

  const restart = async () => {
    await run(async () => {
      checkAuthResult(await signIn.reset());
      checkAuthResult(await signUp.reset());
      setPassword('');
      setCode('');
      setChallenge(null);
      setFirstName('');
      setLastName('');
      setLegalAccepted(false);
      setStep('start');
    });
  };

  const signInWithCode = async () => {
    await run(async () => {
      if (!email.trim()) {
        throw new Error('Enter your email address first.');
      }
      if (signIn.identifier !== email.trim() || !signIn.id) {
        checkAuthResult(await signIn.create({ identifier: email.trim() }));
      }
      const factor = signIn.supportedFirstFactors.find((entry) => { return entry.strategy === 'email_code'; });
      if (!factor) {
        throw new Error('Email-code sign-in is not available for this account. Use your password or Google.');
      }
      await chooseFactor({ kind: 'email', destination: factor.safeIdentifier, factorId: factor.emailAddressId });
    });
  };

  const google = async () => {
    await run(async () => {
      // Keep the redirect state visible after Clerk resolves, until the page leaves.
      setGooglePending(true);
      rememberAuthDestination(destination);
      const params = {
        strategy: 'oauth_google' as const,
        redirectCallbackUrl: authRoute('/sso-callback/', destination),
        redirectUrl: authRoute('/session-tasks/', destination),
      };
      checkAuthResult(await (mode === 'sign-up' ? signUp.sso(params) : signIn.sso(params)));
    });
  };

  const secondFactor = signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust';
  const factors: AuthChallenge[] = [];
  if (secondFactor) {
    for (const factor of signIn.supportedSecondFactors) {
      if (factor.strategy === 'totp') {
        factors.push({ kind: 'totp' });
      } else if (factor.strategy === 'backup_code') {
        factors.push({ kind: 'backup' });
      } else if (factor.strategy === 'email_code') {
        factors.push({ kind: 'mfa-email', destination: factor.safeIdentifier });
      } else if (factor.strategy === 'phone_code') {
        factors.push({ kind: 'mfa-phone', destination: factor.safeIdentifier });
      }
    }
  } else {
    for (const factor of signIn.supportedFirstFactors) {
      if (factor.strategy === 'email_code') {
        factors.push({ kind: 'email', destination: factor.safeIdentifier, factorId: factor.emailAddressId });
      } else if (factor.strategy === 'phone_code') {
        factors.push({ kind: 'phone', destination: factor.safeIdentifier, factorId: factor.phoneNumberId });
      }
    }
  }

  return {
    step, email, setEmail, password, setPassword, code, setCode, firstName, setFirstName, lastName, setLastName,
    legalAccepted, setLegalAccepted, challenge, factors, googlePending, busy: busy || googlePending || !isLoaded || !clerk.loaded,
    error, notice, secondsRemaining, missingFields: signUp.missingFields, requiredFields: signUp.requiredFields,
    submit, resendCode, restart, google, signInWithCode,
    selectFactor: async (factor: AuthChallenge) => { await run(async () => { await chooseFactor(factor); }); },
    showFactors: () => { setCode(''); setError(''); setStep('factors'); },
  };
};
